import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import {
	Box,
	CircularProgress,
	TableCell,
	TableRow,
	tableCellClasses,
	useMediaQuery
} from '@mui/material';
import { IconButton } from '@/components/IconButton';
import { DownloadIcon, RefreshIcon } from '@/components/Icons';
import { formatDashDate, formatPrice, showError, showSuccess } from '@/utils';
import invoiceModel from '@/models/invoice';
import { Tooltip } from '@/components/Tooltip';
import { PAYMENT_STATUS, PAYMENT_TYPE } from '@/models/payment';
import { SUBSCRIPTION_STATUS } from '@/models/subscription';
import { getPriceById } from '@/utils/price';
import { AMEX_SETTLEMENT_DAYS, invoiceDelayHours } from '@/config';
import { setCanRegenerateInvoice } from '@/store/billing';

import { PaymentStatusChip } from './PaymentStatusChip';
import { useUserPermissions } from '../../hooks/hooks';
import { useGetPaymentReference } from './hooks';

export function Payment({
	index,
	createdAt,
	currency,
	amount,
	amountRefunded,
	periodFrom,
	periodTo,
	type,
	priceId,
	handlerId,
	status,
	onRetry,
	storage,
	trial,
	invoiceId,
	isLastPayment,
	handleIncompleteModalOpen = () => {},
	service,
	disputeCurrency,
	settledAt,
	canRegenerateInvoice: canRegenerateInvoiceProp
}) {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const getPaymentReference = useGetPaymentReference();
	const taxInformation = useSelector(
		state => state.auth?.user?.taxInformation
	);
	const prices = useSelector(state => state.checkout?.prices || []);

	const { status: subscriptionStatus } = useSelector(
		state => state.auth?.subscriptionInfo
	);

	const [downloading, setDownloading] = useState(false);
	const [isRegeneratingInvoice, setIsRegeneratingInvoice] = useState(false);

	const smDown = useMediaQuery(theme => theme.breakpoints.down('sm'), {
		noSsr: true
	});
	const { getIsAdminOrOwner } = useUserPermissions();

	const currentPrice = getPriceById(priceId, prices);

	const buttonDisabled = taxInformation && !invoiceId;

	const switchFailed =
		status === PAYMENT_STATUS.ERROR &&
		[SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIALING].includes(
			subscriptionStatus
		) &&
		isLastPayment;

	const hasPayAction =
		status === PAYMENT_STATUS.ERROR &&
		type === PAYMENT_TYPE.PAYMENT &&
		(subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE || switchFailed) &&
		!!(currentPrice?.active || !priceId);

	let TableRowComponent = TableRow;
	let CellComponent = TableCell;
	let BottomComponent = Fragment;
	let bottomProps = {};

	if (smDown) {
		TableRowComponent = Box;
		CellComponent = Box;
		BottomComponent = Box;
		bottomProps = {
			display: 'flex',
			gap: 1,
			mt: '12px',
			pb: '20px',
			borderBottom: '1px solid #B8B8B8',
			alignItems: 'center'
		};
	}

	const canRegenerateInvoice = canRegenerateInvoiceProp && invoiceId;

	const downloadRow = async evt => {
		try {
			if (!taxInformation) {
				evt.preventDefault();

				return handleIncompleteModalOpen();
			}

			setDownloading(true);
			const { url } = await invoiceModel.getDownload(invoiceId);

			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(response.statusText);
			}

			const fileName = `remove-background-invoice-${formatDashDate(periodFrom)}/${formatDashDate(periodTo)}`;

			saveAs(await response.blob(), `${fileName}.pdf`);
			showSuccess(t('billing.downloadInvoiceSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setDownloading(false);
		}
	};

	const regenerateInvoice = async () => {
		try {
			setIsRegeneratingInvoice(true);
			await invoiceModel.regenerate(invoiceId);
			dispatch(setCanRegenerateInvoice({ rowId: index, value: false }));
			showSuccess(t('billing.regenerateInvoiceSuccess'));
		} catch (error) {
			showError(error);
		} finally {
			setIsRegeneratingInvoice(false);
		}
	};

	const showPaymentActions = getIsAdminOrOwner();
	const isTrialDiscountPayment = !!trial && !amount;

	let parsedCurrency = currency;

	if (type === PAYMENT_TYPE.DISPUTE) {
		parsedCurrency = disputeCurrency;
	}

	const pendingInvoiceTitle = !settledAt
		? t('billing.invoiceInProcess.title')
		: t('billing.generatingInvoice');

	const pendingInvoiceDescription = !settledAt
		? t('billing.invoiceInProcess.description', {
				hours: invoiceDelayHours,
				amexSettlementDays: AMEX_SETTLEMENT_DAYS
			})
		: null;

	return (
		<TableRowComponent
			onClick={() =>
				hasPayAction ? onRetry({ priceId, handlerId, currency }) : null
			}
			sx={{
				cursor: hasPayAction ? 'pointer' : 'default',
				[`.${tableCellClasses.root}`]: {
					fontSize: 14
				}
			}}
			component={smDown ? Box : undefined}
		>
			<CellComponent
				sx={{ color: 'text.secondary', mb: { xs: 1, sm: '6px' } }}
			>
				{createdAt ? dayjs.unix(createdAt).format('LL') : '-'}
			</CellComponent>
			<CellComponent
				sx={{ color: 'text.secondary', mb: { xs: 1, sm: '6px' } }}
			>
				{getPaymentReference({ trial, service, storage })}
			</CellComponent>
			<CellComponent sx={{ color: 'text.secondary' }}>
				{periodFrom && periodTo
					? t('billing.history.period', {
							from: dayjs.unix(periodFrom).format('DD-MM-YYYY'),
							to: dayjs.unix(periodTo).format('DD-MM-YYYY')
						})
					: '-'}
			</CellComponent>
			<BottomComponent {...bottomProps}>
				<CellComponent>
					{formatPrice({ amount, currency: parsedCurrency })}
				</CellComponent>
				<CellComponent sx={{ ml: 'auto' }}>
					<PaymentStatusChip
						type={type}
						isLastPayment={isLastPayment}
						status={status}
						amountRefunded={amountRefunded}
						currency={currency}
						hasPayAction={hasPayAction}
					/>
				</CellComponent>
				{showPaymentActions && (
					<CellComponent
						sx={{
							textAlign: 'right'
						}}
					>
						<Box
							sx={{
								minWidth: 42,
								display: 'flex',
								gap: '8px',
								justifyContent: 'flex-end'
							}}
						>
							{canRegenerateInvoice && (
								<Box>
									<IconButton
										aria-label={t('billing.regenerateInvoice')}
										title={t('billing.regenerateInvoice')}
										variant="outlined"
										sx={{ width: 42, height: 42 }}
										onClick={regenerateInvoice}
									>
										{isRegeneratingInvoice ? (
											<CircularProgress size={20} />
										) : (
											<RefreshIcon />
										)}
									</IconButton>
								</Box>
							)}

							{status === PAYMENT_STATUS.PAID && (
								<Tooltip
									large={!settledAt && buttonDisabled}
									placement="top-end"
									title={buttonDisabled ? pendingInvoiceTitle : ''}
									content={pendingInvoiceDescription}
								>
									<Box>
										<IconButton
											aria-label={t('common.download')}
											variant="outlined"
											disabled={
												downloading ||
												buttonDisabled ||
												isTrialDiscountPayment
											}
											sx={{ width: 42, height: 42 }}
											onClick={downloadRow}
										>
											{downloading ? (
												<CircularProgress size={20} />
											) : (
												<DownloadIcon />
											)}
										</IconButton>
									</Box>
								</Tooltip>
							)}
						</Box>
					</CellComponent>
				)}
			</BottomComponent>
		</TableRowComponent>
	);
}
